import agent, { ListData } from "../api/agent";
import { Activity, ActivityFormValues } from "../models/activity";
import { DataTableList } from "../common/interfaces/TableData";

export type ActivityStore = {
  tableData: DataTableList<Activity>;
  selectedActivity?: Activity;
  loadingInitial: boolean;
  editMode: boolean;
  loading: boolean;

  loadActivities: (filters: any, page?: number, pageSize?: number, orderBy?: string) => Promise<void>;
  loadActivity: (id: string) => Promise<Activity | undefined>;
  createActivity: (activity: ActivityFormValues) => Promise<void>;
  updateActivity: (activity: ActivityFormValues) => Promise<void>;
  deleteActivity: (id: string) => Promise<void>;
  clearSelectedActivity: () => void;
};

export const createActivityStore = (set: any, get: any): ActivityStore => ({
  tableData: new DataTableList<Activity>(),
  selectedActivity: undefined,
  loadingInitial: false,
  editMode: false,
  loading: false,

  loadActivities: async (filters: any, page = 1, pageSize = 10, orderBy = "") => {
    set({ activityStore: { ...get().activityStore, loadingInitial: true } });
    try {
      const result: ListData<Activity> = await agent.Activities.list({ filters, page, pageSize, orderBy });
      const list = result.data.reduce((acc: Map<string, Activity>, item: Activity) => {
        acc.set(item.id!, item);
        return acc;
      }, new Map<string, Activity>());
      set({
        activityStore: {
          ...get().activityStore,
          tableData: {
            list,
            currentPage: result.currentPage,
            pageSize: result.pageSize,
            totalRecords: result.totalRecords,
            orderBy: result.orderBy,
          },
          loadingInitial: false,
        },
      });
    } catch (error) {
      console.log(error);
      set({ activityStore: { ...get().activityStore, loadingInitial: false } });
    }
  },

  loadActivity: async (id: string) => {
    const { tableData } = get().activityStore;
    let activity = tableData.list.get(id);
    if (activity) {
      set({ activityStore: { ...get().activityStore, selectedActivity: activity } });
      return activity;
    }
    set({ activityStore: { ...get().activityStore, loadingInitial: true } });
    try {
      activity = await agent.Activities.details(id);
      const updatedList = new Map(tableData.list);
      if (activity?.id) updatedList.set(activity.id, activity);
      set({
        activityStore: {
          ...get().activityStore,
          tableData: { ...tableData, list: updatedList },
          selectedActivity: activity,
          loadingInitial: false,
        },
      });
      return activity;
    } catch (error) {
      console.log(error);
      set({ activityStore: { ...get().activityStore, loadingInitial: false } });
    }
  },

  createActivity: async (activity: ActivityFormValues) => {
    try {
      await agent.Activities.create(activity);
      const newActivity = new Activity(activity);
      const { tableData } = get().activityStore;
      const updatedList = new Map(tableData.list);
      if (newActivity.id) updatedList.set(newActivity.id, newActivity);
      set({
        activityStore: {
          ...get().activityStore,
          tableData: { ...tableData, list: updatedList },
          selectedActivity: newActivity,
        },
      });
    } catch (error) {
      console.log(error);
    }
  },

  updateActivity: async (activity: ActivityFormValues) => {
    try {
      await agent.Activities.update(activity);
      const { tableData } = get().activityStore;
      const updatedList = new Map(tableData.list);
      if (activity.id) {
        const existing = updatedList.get(activity.id);
        updatedList.set(activity.id, { ...(existing || {}), ...activity } as any);
      }
      set({
        activityStore: {
          ...get().activityStore,
          tableData: { ...tableData, list: updatedList },
          selectedActivity: activity.id ? (updatedList.get(activity.id) as Activity) : get().activityStore.selectedActivity,
        },
      });
    } catch (error) {
      console.log(error);
    }
  },

  deleteActivity: async (id: string) => {
    set({ activityStore: { ...get().activityStore, loading: true } });
    try {
      await agent.Activities.delete(id);
      const { tableData } = get().activityStore;
      const updatedList = new Map(tableData.list);
      updatedList.delete(id);
      set({
        activityStore: {
          ...get().activityStore,
          tableData: { ...tableData, list: updatedList },
          loading: false,
        },
      });
    } catch (error) {
      console.log(error);
      set({ activityStore: { ...get().activityStore, loading: false } });
    }
  },

  clearSelectedActivity: () => {
    set({ activityStore: { ...get().activityStore, selectedActivity: undefined } });
  },
});